import * as React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  displayErrorToast,
  displayWarningToast,
} from "src/components/shared/Toasts/Toast";
import { ToastIds } from "src/enums/errorToastIds";
import { useGameContext } from "../../game/useGameContext";
import {
  GameResponseErrors,
  ResponseError,
  ServerResponseErrors,
} from "../../socket/socket.errors";
import { CREATE_ACCOUNT_PATH, GAME, LOGIN_PATH } from "../../utils/routesPaths";
import TextInput from "../shared/Inputs/TextInput/";
import Switch from "../shared/Switch";
import {
  BackgroundWrapper,
  ContinueButton,
  JoinGameWrapper,
  LinksWrapper,
  Spectator,
} from "./JoinGame.components";

interface RouteProps {
  gameId: string;
}

interface IFormValues {
  userName: string;
  isSpectator: boolean;
}

const JoinGame: React.FunctionComponent = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormValues>();
  const { gameId } = useParams<keyof RouteProps>() as RouteProps;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const {
    actionHandlers: { joinGame, getLocalToken, reconnectToGame },
  } = useGameContext();
  const { GAME_NOT_FOUND, USER_ALREADY_CONNECTED, MAX_USERS_LIMIT_REACHED } =
    GameResponseErrors;
  const { AUTH_ERROR } = ServerResponseErrors;

  const onSubmit: SubmitHandler<IFormValues> = async data => {
    const { userName, isSpectator } = data;
    try {
      const response = await joinGame({
        user: {
          name: userName,
          isSpectator,
        },
        gameId,
      });
      if (response.error) {
        displayErrorToast(
          `Couldn't join to game ${response.error.message}`,
          ToastIds.JOIN_GAME_FAILED
        );
      } else {
        navigate(`${GAME}${gameId}`);
      }
    } catch (e) {
      displayErrorToast("Couldn't join to game", ToastIds.JOIN_GAME_FAILED);
    }
  };

  const tryJoining = React.useCallback(async () => {
    const token = getLocalToken() || "";
    try {
      await reconnectToGame({ gameId, token });
      navigate(`${GAME}${gameId}`);
    } catch (e) {
      if (e instanceof ResponseError) {
        switch (e.typename) {
          case GAME_NOT_FOUND:
            navigate("/404");
            break;
          case AUTH_ERROR:
            setIsLoading(false);
            break;
          case USER_ALREADY_CONNECTED:
            displayErrorToast(e.message, ToastIds.USER_ALREADY_CONNECTED);
            navigate("/404");
            break;
          case MAX_USERS_LIMIT_REACHED:
            displayWarningToast("This lobby is full!", ToastIds.LOBBY_IS_FULL);
            break;
          default:
            displayErrorToast(e.message, ToastIds.RECONNECT_ERROR);
        }
      }
    }
  }, [
    getLocalToken,
    reconnectToGame,
    gameId,
    history,
    GAME_NOT_FOUND,
    AUTH_ERROR,
    USER_ALREADY_CONNECTED,
    MAX_USERS_LIMIT_REACHED,
  ]);

  React.useEffect(() => {
    tryJoining();
  }, [tryJoining]);

  if (isLoading) {
    return null;
  }

  return (
    <BackgroundWrapper>
      <JoinGameWrapper>
        <h2>Choose your display name</h2>
        <form onSubmit={handleSubmit(onSubmit)} aria-label="join-game-form">
          <TextInput
            register={register("userName", {
              required: { value: true, message: "Required" },
              maxLength: { value: 24, message: "Name too long" },
            })}
            aria="Your display name"
            label="Your display name"
            errorMessage={errors.userName?.message}
          />
          <Spectator>
            Join as spectator <Switch register={register("isSpectator")} />
          </Spectator>
          <ContinueButton themeColor="buttonPrimary">
            Continue to game
          </ContinueButton>
        </form>
        <LinksWrapper>
          <Link
            aria-label="Create account"
            className="text-link"
            to={CREATE_ACCOUNT_PATH}>
            Create account
          </Link>
          <Link aria-label="Login" className="text-link" to={LOGIN_PATH}>
            Login
          </Link>
        </LinksWrapper>
      </JoinGameWrapper>
    </BackgroundWrapper>
  );
};

export default JoinGame;
